import React, { useState, useCallback, RefObject } from 'react';
import { findEditableParent } from '../utils/domUtils';

interface UseContentEditingProps {
  isEditMode: boolean;
  contentRef: RefObject<HTMLDivElement>;
  onContentChange: (content: string) => void;
}

export const useContentEditing = ({ isEditMode, contentRef, onContentChange }: UseContentEditingProps) => {
  const [selectedElement, setSelectedElement] = useState<HTMLElement | null>(null);
  const [hoveredElement, setHoveredElement] = useState<HTMLElement | null>(null);

  const handleContentMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (!isEditMode) return;

      const target = e.target as HTMLElement;
      if (target === contentRef.current) return;

      console.log('[mousedown]', target);

      const editableElement = findEditableParent(target);

      if (editableElement) {
        console.log('Found editable element', editableElement);
        console.log('Inner text', editableElement.innerText);

        // If clicking on already selected element, let the browser handle cursor placement
        if (selectedElement === editableElement && editableElement.contentEditable === 'true') {
          console.log('already selected element mousedown');
          return;
        }

        // Clean up previous selection
        if (selectedElement && selectedElement !== editableElement) {
          console.log('cleaning up previous selection', selectedElement);
          selectedElement.contentEditable = 'false';
          selectedElement.classList.remove('editing');
        }

        // Activate new element
        editableElement.contentEditable = 'true';
        editableElement.classList.add('editing');

        if (editableElement !== document.activeElement) {
          editableElement.focus();
        }

        setSelectedElement(editableElement);
      }
    },
    [isEditMode, selectedElement, contentRef],
  );

  const handleContentMouseOver = useCallback(
    (e: React.MouseEvent) => {
      if (!isEditMode) return;

      e.stopPropagation();
      const target = e.target as HTMLElement;

      if (target === contentRef.current) return;

      const editableElement = findEditableParent(target);

      if (editableElement) {
        if (hoveredElement && hoveredElement !== editableElement) {
          hoveredElement.classList.remove('hover');
        }
        editableElement.classList.add('hover');
        setHoveredElement(editableElement);
      }
    },
    [isEditMode, hoveredElement, contentRef],
  );

  const handleContentMouseOut = useCallback(() => {
    if (hoveredElement) {
      hoveredElement.classList.remove('hover');
      setHoveredElement(null);
    }
  }, [hoveredElement]);

  const handleContentBlur = useCallback(
    (e: React.FocusEvent) => {
      const target = e.target as HTMLElement;
      console.log('[blurred]', target);

      if (target.contentEditable === 'true') {
        // Check if the content is truly empty (no text AND no other elements like images)
        const textContent = target.innerText?.trim();
        const hasChildElements = target.querySelector('img, video, iframe, object, embed, svg');

        if ((textContent === '' || textContent === '\u00A0') && !hasChildElements) {
          // Only replace with zero-width space if there are no media elements
          target.innerHTML = '\u200B';
        }

        target.contentEditable = 'false';
        target.classList.remove('editing');
        setSelectedElement(null);

        const related = e.relatedTarget as Node | null;
        if (related && (related as HTMLElement).closest('.link-edit-popover')) {
          console.log('clicked link popover');
          return; // don't commit content when clicking link popover
        }

        if (contentRef.current) {
          const updatedContent = contentRef.current.innerHTML;
          onContentChange(updatedContent);
        }
      }
    },
    [contentRef, onContentChange],
  );

  const handleContentKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      const target = e.target as HTMLElement;
      if (target.contentEditable === 'true') {
        e.preventDefault();
        target.blur();
      }
    }
  }, []);

  return {
    selectedElement,
    hoveredElement,
    handleContentMouseDown,
    handleContentMouseOver,
    handleContentMouseOut,
    handleContentBlur,
    handleContentKeyDown,
  };
};
